import React, {
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
} from 'react-router-dom';

import './OpportunitySourcesPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const SOURCES_URL =
  `${API_BASE_URL}/api/v1/opportunity_sources`;

const formatDate = (value) => {
  if (!value) return 'Never checked';

  return new Intl.DateTimeFormat(
    'en',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
  ).format(new Date(value));
};

const OpportunitySourcesPage = () => {
  const [sources, setSources] =
    useState([]);

  const [status, setStatus] =
    useState('loading');

  const [busyId, setBusyId] =
    useState(null);

  const [message, setMessage] =
    useState('');

  const [error, setError] =
    useState('');

  const loadSources = async () => {
    try {
      setStatus('loading');
      setError('');

      const response =
        await axios.get(SOURCES_URL);

      setSources(response.data);
      setStatus('ready');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to load sources.'
      );

      setStatus('failed');
    }
  };

  useEffect(() => {
    loadSources();
  }, []);

  const toggleActive = async (id) => {
    try {
      setBusyId(id);
      setMessage('');
      setError('');

      const response =
        await axios.patch(
          `${SOURCES_URL}/${id}/toggle_active`
        );

      setSources((current) =>
        current.map((source) =>
          source.id === id
            ? response.data
            : source
        )
      );
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to update source.'
      );
    } finally {
      setBusyId(null);
    }
  };

  const runCheck = async (source) => {
    try {
      setBusyId(source.id);
      setMessage('');
      setError('');

      const response =
        await axios.post(
          `${SOURCES_URL}/${source.id}/check`
        );

      if (response.data.success) {
        setMessage(
          `${source.name}: check successful — ` +
          `${response.data.items_found} items found.`
        );
      } else {
        setError(
          `${source.name}: ${response.data.error}`
        );
      }

      await loadSources();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        requestError.message ||
        'Source check failed.'
      );
    } finally {
      setBusyId(null);
    }
  };

  if (status === 'loading') {
    return (
      <main className="admin-page">
        <p>Loading opportunity sources...</p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Opportunity monitoring
          </p>

          <h1>Sources</h1>

          <p>
            Manage the organizations, RSS feeds
            and websites used by ACE to discover
            opportunities.
          </p>
        </div>

        <Link
          to="/admin/opportunity-sources/new"
          className="admin-primary-header-action"
        >
          + Add source
        </Link>
      </header>

      {message && (
        <div className="source-message success">
          {message}
        </div>
      )}

      {error && (
        <div className="source-message error">
          {error}
        </div>
      )}

      <section className="source-summary-grid">
        <article>
          <strong>{sources.length}</strong>
          <span>Total sources</span>
        </article>

        <article>
          <strong>
            {
              sources.filter(
                (source) => source.active
              ).length
            }
          </strong>
          <span>Active sources</span>
        </article>

        <article>
          <strong>
            {
              sources.reduce(
                (total, source) =>
                  total +
                  source.discovered_count,
                0
              )
            }
          </strong>
          <span>Awaiting review</span>
        </article>

        <article>
          <strong>
            {
              sources.reduce(
                (total, source) =>
                  total +
                  source.published_count,
                0
              )
            }
          </strong>
          <span>Published</span>
        </article>
      </section>

      <section className="sources-list">
        {sources.map((source) => (
          <article
            key={source.id}
            className="source-card"
          >
            <div className="source-card-main">
              <div className="source-card-heading">
                <div>
                  <div className="source-badges">
                    <span className="source-type">
                      {source.source_type}
                    </span>

                    <span
                      className={
                        source.active
                          ? 'source-active'
                          : 'source-inactive'
                      }
                    >
                      {source.active
                        ? 'Active'
                        : 'Inactive'}
                    </span>
                  </div>

                  <h2>{source.name}</h2>

                  <a
                    href={source.base_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {source.base_url}
                  </a>
                </div>

                <Link
                  to={
                    `/admin/opportunity-sources/` +
                    `${source.id}/edit`
                  }
                  className="source-edit-link"
                >
                  Edit
                </Link>
              </div>

              {source.feed_url && (
                <div className="source-feed">
                  <span>Feed</span>
                  <code>{source.feed_url}</code>
                </div>
              )}

              <div className="source-metrics">
                <div>
                  <strong>
                    {source.opportunities_count}
                  </strong>
                  <span>Imported</span>
                </div>

                <div>
                  <strong>
                    {source.discovered_count}
                  </strong>
                  <span>Discovered</span>
                </div>

                <div>
                  <strong>
                    {source.published_count}
                  </strong>
                  <span>Published</span>
                </div>

                <div>
                  <strong>
                    {formatDate(
                      source.last_checked_at
                    )}
                  </strong>
                  <span>Last check</span>
                </div>
              </div>
            </div>

            <footer className="source-actions">
              <button
                type="button"
                onClick={() =>
                  runCheck(source)
                }
                disabled={busyId === source.id}
                className="source-button check"
              >
                {busyId === source.id
                  ? 'Checking...'
                  : 'Run check now'}
              </button>

              <button
                type="button"
                onClick={() =>
                  toggleActive(source.id)
                }
                disabled={busyId === source.id}
                className="source-button toggle"
              >
                {source.active
                  ? 'Deactivate'
                  : 'Activate'}
              </button>
            </footer>
          </article>
        ))}

        {sources.length === 0 && (
          <div className="sources-empty">
            <h2>No sources yet</h2>
            <p>
              Add the first opportunity source
              to start monitoring.
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default OpportunitySourcesPage;
